'use client';
import React, { useRef } from 'react';
import { useIntersectionObserver } from '@benzinga/hooks';

interface YouTubeVideoPlayerProps {
  videoId: string;
  title: string;
}

export const YouTubeVideoPlayer: React.FC<YouTubeVideoPlayerProps> = ({ title, videoId }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const entry = useIntersectionObserver(containerRef as React.RefObject<Element>, {
    freezeOnceVisible: true,
    rootMargin: '200px',
    threshold: 0,
  });
  const inView = Boolean(entry?.isIntersecting);

  // youtube.com/embed (not youtube-nocookie) to match the rest of the site's embeds,
  // which is what the page CSP allows in frame-src. No autoplay — click to play.
  const embedSrc = `https://www.youtube.com/embed/${videoId}?playsinline=1&rel=0`;

  return (
    <div className="relative w-full aspect-[16/9] overflow-hidden rounded-lg bg-black" ref={containerRef}>
      {inView ? (
        <iframe
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          className="absolute inset-0 h-full w-full border-0"
          loading="lazy"
          referrerPolicy="strict-origin-when-cross-origin"
          src={embedSrc}
          title={title}
        />
      ) : (
        <img
          alt={title}
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
          src={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`}
        />
      )}
    </div>
  );
};
